import { MIN_INVESTMENT_AMOUNT, MIN_INVESTMENT_AMOUNT_SEK } from '../constants/profileFieldLimits';

/** A new profile stores [0, 0]. That maximum is unset, not a real cap. */
export function investmentMaxUnset(isEmpty: boolean, range: [number, number] | undefined): boolean {
  return isEmpty || !(typeof range?.[1] === 'number' && range[1] > 0);
}

/**
 * Typing a minimum into an unfinished range used to copy that number into the maximum.
 * Currency change then converted both. An unset maximum stays 0.
 */
export function rangeAfterMinChange(
  range: [number, number],
  nextMin: number,
  maxUnset: boolean
): [number, number] {
  return maxUnset ? [nextMin, 0] : [nextMin, Math.max(range[1], nextMin)];
}

export function shouldConvertFinishedRange(maxUnset: boolean): boolean {
  return !maxUnset;
}

/**
 * Currency toggle keeps the typed numbers. Only raise a set end if it is
 * below that currency’s floor (currently $50,000 / SEK 500,000).
 */
export function keepRangeOnCurrencyChange(
  range: [number, number],
  floor: number
): [number, number] {
  const nextMin = range[0] > 0 ? Math.max(range[0], floor) : range[0];
  const nextMax = range[1] > 0 ? Math.max(range[1], nextMin) : range[1];
  return [nextMin, nextMax];
}

/** A saved range still converts. The slider max is the EUR cap scaled by the destination rate. */
export function convertFinishedInvestmentRange(
  range: [number, number],
  rateFrom: number,
  rateTo: number,
  sliderMaxInEur: number
): [number, number] {
  const convertedMin = Math.round((range[0] / rateFrom) * rateTo);
  const convertedMax = Math.round((range[1] / rateFrom) * rateTo);
  const newDynamicMax = Math.round(sliderMaxInEur * rateTo);
  return [Math.min(convertedMin, newDynamicMax), Math.min(convertedMax, newDynamicMax)];
}

/** Investor range floor in the selected currency ($50k / SEK 500k). */
export function investorInvestmentRangeFloor(currency: string): number {
  return (currency || '').toLowerCase() === 'sek'
    ? MIN_INVESTMENT_AMOUNT_SEK
    : MIN_INVESTMENT_AMOUNT;
}

/**
 * Equal ends are a real range (100,000 to 100,000). An unset maximum stays 0.
 * Pass `floor` (e.g. 50,000) so values like 30,000 cannot save.
 */
export function investmentRangeIsSaveable(range: [number, number], floor = 0): boolean {
  return range[0] >= floor && range[1] > 0 && range[1] >= range[0];
}

/** The industry step starts blank until the investor edits, and still shows a saved range. */
export function industryRangeIsEmpty(rangeEdited: boolean, range: [number, number]): boolean {
  return !rangeEdited && !investmentRangeIsSaveable(range);
}
